import {
  ANIM_DELAY,
  HEX_LINE_WIDTH,
  ANIMATION_MODES,
  COMPUTER_USERID,
} from "../constants_v2.js";
import { colors } from "../colors_v2.js";
import { board } from "./gameBoard_v2.js";
import {
  calculateBorderColor,
  getNeighbor,
  gameCoordState,
  gameCoords2Pixels,
} from "./utils-game_v2.js";
import { zoom } from "../zoom_v2.js";
import { matchService } from "../matches/matchService_v2.js";
import { identityService } from "../profile/identityService_v2.js";

const animationState = {
  animationRunning: false,
};

export const isAnimationModeValid = (mode) => {
  if (ANIMATION_MODES.includes(mode)) {
    return true;
  } else {
    return false;
  }
};

export const isAnimationRunning = () => {
  return animationState.animationRunning;
};

export const setAnimationRunning = (state) => {
  if ([true, false].includes(state)) {
    animationState.animationRunning = state;
  } else {
    console.log(
      "Error: Attempt to set animationRunning to an invalid value: " + state,
    );
  }
  if (state === false) {
    const match = matchService.getCurrentMatch().currentMatch;
    if (match.turn === COMPUTER_USERID) {
      board.render();
    }
  }
};

const behindParams = ({ pieces2Move, i, from }) => {
  const drawParams = {
    borderColor: colors.getGameColor("background"),
    fillColor:
      i === 0
        ? colors.getGameColor("background")
        : colors.getPlayerColor({
            player: pieces2Move[i - 1].player,
            property: "pieceGray",
          }),
    coords: from.coords,
  };
  const dirParams = {
    color:
      i === 0
        ? colors.getGameColor("background")
        : colors.getPlayerColor({
            player: pieces2Move[i - 1].player,
            property: "directionGray",
          }),
    direction: from.direction,
    coords: from.coords,
  };
  return { drawParams, dirParams };
};

const movingParams = ({ from }) => {
  const drawParams = {
    borderColor: colors.getGameColor("background"),
    fillColor: colors.getPlayerColor({
      player: from.player,
      property: "piece",
    }),
  };
  const dirParams = {
    direction: from.direction,
    color: colors.getPlayerColor({
      player: from.player,
      property: "direction",
    }),
  };
  return { drawParams, dirParams };
};

export const calculateFillColorGray = (coords) => {
  const { player } = gameCoordState(coords);
  switch (player) {
    case "playerTwo":
      return colors.getPlayerColor({
        player: "playerTwo",
        property: "pieceGray",
      });
    case "playerOne":
      return colors.getPlayerColor({
        player: "playerOne",
        property: "pieceGray",
      });
    case "Empty":
      return colors.getGameColor("background");
  }
};

const neighborParams = ({ from, direction }) => {
  const piece = getNeighbor({
    coords: from.coords,
    direction: direction,
  });
  const drawParams = {
    borderColor:
      piece.player === "Empty"
        ? colors.getGameColor("hexGrid")
        : calculateBorderColor(piece.coords),
    fillColor: calculateFillColorGray(piece.coords),
    coords: piece.coords,
  };
  const dirParams = {
    color:
      piece.player === "Empty"
        ? colors.getGameColor("background")
        : colors.getPlayerColor({
            player: piece.player,
            property: "directionGray",
          }),
    direction: piece.direction,
    coords: piece.coords,
  };
  return { drawParams, dirParams, piece };
};

const neighborsParams = ({ from }) => {
  return {
    up: neighborParams({ from, direction: "up" }),
    upLeft: neighborParams({ from, direction: "upLeft" }),
    upRight: neighborParams({ from, direction: "upRight" }),
    down: neighborParams({ from, direction: "down" }),
    downRight: neighborParams({ from, direction: "downRight" }),
    downLeft: neighborParams({ from, direction: "downLeft" }),
  };
};

const drawNeighbors = ({ params, neighbors }) => {
  for (const neighbor of neighbors) {
    board.drawHexagonOnGrid(params[neighbor].drawParams);
    if (params[neighbor].piece.player != "Empty") {
      board.drawDirectionOnGrid(params[neighbor].dirParams);
    }
  }
};

/**
 * @brief Finalizes a piece's movement in a chain and manages temporary rendering.
 * @description Updates the game service with new coordinates and handles
 * the 'ghost' rendering for pieces pushed in a chain (where i > 0).
 * @async
 * @param {Object} params - Context for the piece movement.
 * @param {Object} params.from - The source piece data and its array index.
 * @param {Object} params.to - The destination piece/coordinates data.
 * @param {Array} params.pieces2Move - The full sequence of the move plan.
 * @param {number} params.i - The current iteration index in the movement chain.
 */
const drawInNewSpot = async ({ from, to, pieces2Move, i }) => {
  // 1. Data Persistence: Update the piece's location in the match service
  await persistPieceMovement({ from, to });

  // 2. Visual Management:
  // We skip i == 0 because that piece is the 'active' placement handled
  // by the final board render. i > 0 handles the chain-reaction pieces.
  if (i > 0) {
    renderChainGhost({
      from,
      pushedByPlayer: pieces2Move[i - 1].player,
    });
  }
};

/**
 * @brief Communicates the coordinate change to the matchService.
 * @async
 * @param {Object} params - The from/to context for the update.
 */
const persistPieceMovement = async ({ from, to }) => {
  const pieceToUpdate = {
    player: from.piece.player,
    direction: from.piece.direction,
    coords: to.piece.coords,
  };

  await matchService.updatePiece({
    index: from.index,
    piece: pieceToUpdate,
  });
};

/**
 * @brief Renders a grayed-out version of a piece during a chain move.
 * @description Draws a piece in its previous location using 'gray' theme colors
 * to show the result of a push before the final global re-render.
 * @param {Object} params - Rendering metadata.
 */
const renderChainGhost = ({ from, pushedByPlayer }) => {
  const coords = from.piece.coords;
  const direction = from.piece.direction;

  // Draw the body of the hex in the "ghosted" player color
  board.drawHexagonOnGrid({
    coords: coords,
    fillColor: colors.getPlayerColor({
      player: pushedByPlayer,
      property: "pieceGray",
    }),
    borderColor: colors.getGameColor("hex_available"),
  });

  // Draw the direction indicator in the "ghosted" player color
  board.drawDirectionOnGrid({
    coords: coords,
    direction: direction,
    color: colors.getPlayerColor({
      player: pushedByPlayer,
      property: "directionGray",
    }),
  });
};

/**
 * @brief Animates a single piece moving from one hex to another.
 * @description Calculates directional offsets, schedules pixel-by-pixel frame
 * rendering, and finalizes the piece's position in the game state.
 * @async
 * @param {Object} params - Animation parameters.
 * @returns {Promise<number>} The final accumulated animation delay.
 */
export const animatePiece = async ({
  i,
  animDelayMs,
  pieces2Move,
  from,
  to,
}) => {
  const isSerial = identityService.getAnimationMode() === "serial";
  const isOff = identityService.getAnimationMode() === "off";

  if (!isSerial) animDelayMs = 0;

  const animationDistance = getAnimationDistance(from.piece.direction);
  const behind = behindParams({ pieces2Move, i, from: from.piece });
  const moving = movingParams({ from: from.piece });

  // 1. Pixel-by-pixel loop to schedule frames
  for (let loop = 0; loop < animationDistance; loop++) {
    scheduleFrame({
      loop,
      animDelayMs,
      from,
      i,
      behind,
      moving,
    });

    if (!isOff) {
      animDelayMs += ANIM_DELAY;
    }
  }

  // 2. Schedule final placement once animation completes
  setTimeout(async () => {
    await drawInNewSpot({ from, to, pieces2Move, i });
  }, animDelayMs);

  return animDelayMs;
};

/**
 * @brief Determines the total pixel distance for a hex-to-hex move.
 * @param {string} direction - The move direction (up, down, etc).
 * @returns {number} The distance in pixels.
 */
const getAnimationDistance = (direction) => {
  const isVertical = direction === "up" || direction === "down";
  const sideLength = zoom.getHexSide();

  return isVertical ? sideLength * 2 - HEX_LINE_WIDTH * 2 : sideLength * 1.5;
};

/**
 * @brief Calculates the canvas coordinate offset based on movement direction.
 * @param {Object} params - Input coordinates and loop progress.
 * @returns {Object} The new cx and cy pixel coordinates.
 */
const calculateMovementOffset = ({ from, direction, loop }) => {
  const Y_RATIO = 0.6; // Constant for diagonal Y movement
  const offsets = {
    up: { cx: from.cx, cy: from.cy - loop },
    down: { cx: from.cx, cy: from.cy + loop },
    upRight: { cx: from.cx + loop, cy: from.cy - loop * Y_RATIO },
    downRight: { cx: from.cx + loop, cy: from.cy + loop * Y_RATIO },
    downLeft: { cx: from.cx - loop, cy: from.cy + loop * Y_RATIO },
    upLeft: { cx: from.cx - loop, cy: from.cy - loop * Y_RATIO },
  };
  return offsets[direction];
};

/**
 * @brief Schedules a single pixel of movement on the animation timeline.
 * @param {Object} params - Context for the current frame.
 */
const scheduleFrame = ({ loop, animDelayMs, from, i, behind, moving }) => {
  setTimeout(async () => {
    await new Promise(requestAnimationFrame);

    const currentCoords = calculateMovementOffset({
      from,
      direction: from.piece.direction,
      loop,
    });

    refreshBackgroundLayer({ from, currentCoords, i, behind, moving });

    // Draw the moving piece on top
    moving.drawParams.canvasCoords = currentCoords;
    moving.dirParams.canvasCoords = currentCoords;

    board.drawHexagonOnCanvas(moving.drawParams);
    board.drawDirectionOnCanvasNew(moving.dirParams);
  }, animDelayMs);
};

/**
 * @brief Cleans up and redraws the background elements affected by movement.
 * @description Repaints neighbors and fixed UI elements to prevent "ghosting".
 */
const refreshBackgroundLayer = ({ from, i, behind }) => {
  const neighborMap = {
    up: ["upRight", "upLeft"],
    down: ["downRight", "downLeft"],
    upRight: ["up", "downRight"],
    downRight: ["upRight", "down"],
    downLeft: ["upLeft", "down"],
    upLeft: ["downLeft", "up"],
  };

  // 1. Draw background neighbors the piece is moving over
  drawNeighbors({
    params: neighborsParams({ from: from.piece }),
    neighbors: neighborMap[from.piece.direction],
  });

  // 2. Draw the "behind" state (the hex the piece left)
  board.drawHexagonOnGrid(behind.drawParams);
  if (i > 0) {
    board.drawDirectionOnGrid(behind.dirParams);
  }

  // 3. Keep "Available" move markers visible
  board.drawTheAvailables({
    availables: board.getAvailables(),
    borderColor: colors.getGameColor("hex_available"),
  });
};

// rotate animation stuff

/**
 * @brief Maps direction strings to degrees for rotation math.
 * @param {string} direction - e.g., "up", "upRight"
 * @returns {number} Angle in degrees.
 */
const getDirectionAngle = (direction) => {
  const angles = {
    up: 0,
    upRight: 60,
    downRight: 120,
    down: 180,
    downLeft: 240,
    upLeft: 300,
  };
  return angles[direction] ?? 0;
};

/**
 * @brief Animates the visual rotation of a piece with a smooth Ease-In-Ease-Out effect.
 * @async
 */
export const animateRotation = async ({ coords, startDir, endDir, player }) => {
  const startAngle = getDirectionAngle(startDir);
  let endAngle = getDirectionAngle(endDir);

  // Shortest path normalization
  if (endAngle - startAngle > 180) endAngle -= 360;
  if (endAngle - startAngle < -180) endAngle += 360;

  const totalFrames = 20; // Increased slightly for a smoother curve
  const canvasPos = gameCoords2Pixels(coords);
  const totalChange = endAngle - startAngle;

  for (let frame = 0; frame <= totalFrames; frame++) {
    // Calculate normalized progress (0 to 1)
    const t = frame / totalFrames;

    // Apply Ease-In-Ease-Out (Sinusoidal)
    // Formula: (1 - cos(pi * t)) / 2
    const easedT = (1 - Math.cos(Math.PI * t)) / 2;

    // Calculate the actual angle for this frame
    const currentAngle = startAngle + totalChange * easedT;

    await new Promise(requestAnimationFrame);

    renderRotationFrame({
      coords,
      canvasPos,
      angle: currentAngle,
      player,
    });
  }
};

/**
 * @brief Renders a single frame of a rotation animation.
 * @param {Object} params - Frame data.
 */
const renderRotationFrame = ({ coords, canvasPos, angle, player }) => {
  //  Clear the specific hex spot so we don't get "smearing"
  board.drawHexagonOnGrid({
    coords,
    fillColor: colors.getPlayerColor({ player, property: "piece" }),
    borderColor: colors.getGameColor("hexGrid"),
  });

  // Draw the direction arrow at the custom angle
  board.drawDirectionOnCanvasNew({
    canvasCoords: canvasPos,
    angle: angle,
    color: colors.getPlayerColor({ player, property: "direction" }),
  });
};

/**
 * @brief Animates a piece shrinking and fading out.
 * @async
 * @param {Object} params
 * @param {Object} params.coords - The hex to clear.
 * @param {string} params.player - The owner of the piece.
 */
export const animateRemoval = async ({ coords, player }) => {
  const totalFrames = 15;
  const canvasPos = gameCoords2Pixels(coords);

  for (let frame = 0; frame <= totalFrames; frame++) {
    const t = frame / totalFrames;

    // We want it to start at 1 (full size) and go to 0 (gone)
    // Using a "Power" easing for a snappy "pop" feel
    const scale = 1 - t * t;
    const opacity = 1 - t;

    await new Promise(requestAnimationFrame);

    renderRemovalFrame({
      coords,
      canvasPos,
      player,
      scale,
      opacity,
    });
  }
};

/**
 * @brief Renders a single frame of the removal animation.
 */
const renderRemovalFrame = ({ coords, canvasPos, player, scale, opacity }) => {
  const ctx = canvas.getContext("2d");
  // 1. Clear the area by drawing the empty board hex/background first
  board.drawHexagonOnGrid({
    coords,
    fillColor: colors.getGameColor("background"),
    borderColor: colors.getGameColor("hexGrid"),
  });

  // 2. Draw the "shrinking" piece
  // We use ctx.scale to handle the size change easily
  ctx.save();
  ctx.translate(canvasPos.cx, canvasPos.cy);
  ctx.scale(scale, scale);
  ctx.globalAlpha = opacity;

  // Draw the piece body (centered at 0,0 now due to translate)
  board.drawHexagonOnCanvas({
    canvasCoords: { cx: 0, cy: 0 },
    fillColor: colors.getPlayerColor({ player, property: "piece" }),
  });

  // Draw the direction indicator (centered at 0,0)
  // We can use our new rotation-friendly function here!
  board.drawDirectionOnCanvasNew({
    canvasCoords: { cx: 0, cy: 0 },
    color: colors.getPlayerColor({ player, property: "direction" }),
    direction: gameCoordState(coords).direction,
  });

  ctx.restore();
};
